/**
 * Module key → display label + route path (CLARID-UX-2026-06, PR 2).
 *
 * Bridges the help-content {@link ModuleKey} space to the app's routes so the
 * cross-module graph (`feeds` / `needs`) can render as real links. Labels and
 * paths mirror `components/navigation/navConfig.ts`; keep them in sync if a nav
 * label or href changes.
 *
 * Typed as `Record<ModuleKey, …>` so the type-checker guarantees every module
 * has a route — no broken "this connects to…" links.
 */

import type { ModuleKey } from './keys';

export interface ModuleRoute {
  /** User-facing label, matching the nav where one exists. */
  label: string;
  /** Route path to link to. */
  path: string;
}

export const moduleRoutes: Record<ModuleKey, ModuleRoute> = {
  // Marketing
  'marketing-upload': { label: 'Content Review', path: '/upload' },
  'marketing-reports': { label: 'Marketing Reports', path: '/reports' },
  'content-library': { label: 'Content Library', path: '/content-library' },
  'pending-reviews': { label: 'Approval Workflow', path: '/pending-reviews' },
  'website-scan': { label: 'Website Scan', path: '/website-scan' },
  'social-media': { label: 'Social Media', path: '/social-media' },
  'mcp-checks': { label: 'MCP Checks', path: '/mcp-checks' },
  'marketing-analytics': { label: 'Marketing Analytics', path: '/analytics' },
  // HMDA
  'hmda-lar-builder': { label: 'LAR Builder', path: '/hmda/lar-builder' },
  'hmda-field-guide': { label: 'LO Field Guide', path: '/hmda/field-guide' },
  'hmda-lar-validation': { label: 'LAR Validation', path: '/hmda/lar-validation' },
  'hmda-edit-intelligence': { label: 'Edit Intelligence', path: '/hmda/edit-intelligence' },
  'hmda-geocoding': { label: 'Geocoding Validation', path: '/hmda/geocoding' },
  'hmda-apor': { label: 'APOR Rate Spread', path: '/hmda/apor' },
  'hmda-cfpb-filing': { label: 'CFPB Filing', path: '/hmda/cfpb-filing' },
  'hmda-quality': { label: 'HMDA Data Quality', path: '/hmda/quality' },
  // Fair Lending
  'fair-lending': { label: 'Fair Lending (Denial & Pricing)', path: '/fair-lending' },
  'alert-upload': { label: 'Loan Trial Balance (ALERT/ILDR)', path: '/alert/upload' },
  'fair-lending-risk-assessments': { label: 'Risk Assessments', path: '/fair-lending/risk-assessments' },
  'fair-lending-loan-officers': { label: 'LO Monitoring', path: '/fair-lending/loan-officers' },
  'fair-lending-redlining': { label: 'Redlining Analysis', path: '/fair-lending/redlining' },
  'fair-lending-redlining-map': { label: 'Redlining Map', path: '/fair-lending/redlining-map' },
  'fair-lending-regression': { label: 'Regression Analysis', path: '/fair-lending/regression' },
  'fair-lending-content-findings': { label: 'Content Screening', path: '/fair-lending/content-findings' },
  exceptions: { label: 'Exception Tracking', path: '/exceptions' },
  // CRA
  cra: { label: 'CRA Dashboard', path: '/cra' },
  'cra-assessment-areas': { label: 'Assessment Areas', path: '/cra/assessment-areas' },
  'cra-sb-loans': { label: 'Small Business Loans', path: '/cra/sb-loans' },
  'cra-lending-analysis': { label: 'Lending Analysis', path: '/cra/lending-analysis' },
  'cra-lending-test': { label: 'Lending Test', path: '/cra/lending-test' },
  'cra-cd-activities': { label: 'CD Activities', path: '/cra/cd-activities' },
  'cra-gap-analysis': { label: 'Gap Analysis', path: '/cra/gap-analysis' },
  'cra-public-file': { label: 'Public File', path: '/cra/public-file' },
  'crad-report': { label: 'CRAD Report', path: '/cra' },
  'cra-large-bank': { label: 'Large Bank Evaluation', path: '/cra/large-bank' },
  // Exam Center / Regulatory Intelligence
  'exam-readiness': { label: 'Exam Readiness', path: '/exam-readiness' },
  exams: { label: 'Exam Management', path: '/exams' },
  'compliance-hub': { label: 'Compliance Hub', path: '/compliance-hub' },
  'regulatory-intelligence': { label: 'Regulatory Changes', path: '/regulatory-intelligence' },
  'enforcement-intelligence': { label: 'Enforcement Intelligence', path: '/enforcement-intelligence' },
  'state-compliance': { label: 'State-Specific Rules', path: '/state-compliance' },
  // Program / Platform / Shell
  calendar: { label: 'Calendar & Tasks', path: '/calendar' },
  'board-reports': { label: 'Board Reporting', path: '/board-reports' },
  'peer-benchmarking': { label: 'Peer Benchmarking', path: '/peer-benchmarking' },
  policies: { label: 'Policies', path: '/policies' },
  procedures: { label: 'Procedure Review', path: '/procedures' },
  complaints: { label: 'Complaints', path: '/complaints' },
  'vendor-compliance': { label: 'Vendor Compliance', path: '/vendor-compliance' },
  training: { label: 'Training', path: '/training' },
  regulations: { label: 'Knowledge Base', path: '/regulations' },
  'product-profiles': { label: 'Product Profiles', path: '/product-profiles' },
  'custom-rules': { label: 'Custom Rules', path: '/custom-rules' },
  'ai-performance': { label: 'AI Performance', path: '/ai-performance' },
  dashboard: { label: 'Dashboard', path: '/dashboard' },
  branches: { label: 'Branches', path: '/branches' },
  organization: { label: 'Organization', path: '/organization' },
};

/** CRAD lives at a dynamic `/cra/crad/:id`; `crad-report`'s nav path is the
 * `/cra` fallback, which would otherwise collapse the CRAD detail page onto the
 * CRA dashboard. Resolve it explicitly. */
const CRAD_DETAIL_PREFIX = '/cra/crad';

/**
 * Resolve a pathname to the module it belongs to, for the route-driven intro
 * mount (PR 3). Longest-prefix match over {@link moduleRoutes} so detail routes
 * (e.g. `/board-reports/:id`) resolve to their parent module. Returns
 * `undefined` for paths with no help entry (Settings, Billing, etc.).
 */
export function resolveModuleKey(pathname: string): ModuleKey | undefined {
  if (pathname === CRAD_DETAIL_PREFIX || pathname.startsWith(`${CRAD_DETAIL_PREFIX}/`)) {
    return 'crad-report';
  }

  let best: ModuleKey | undefined;
  let bestLen = -1;
  for (const key of Object.keys(moduleRoutes) as ModuleKey[]) {
    const { path } = moduleRoutes[key];
    const matches = pathname === path || pathname.startsWith(`${path}/`);
    if (matches && path.length > bestLen) {
      best = key;
      bestLen = path.length;
    }
  }
  return best;
}
